import { useCallback } from 'react';

import { useRouter } from 'next/navigation';

import { clearEnrollmentTracking, findEnrollmentPackItemsInCart } from '@/helpers/utils/enrollmentFlowTracker';
import { clearAllLocalStorage } from '@/helpers/utils/localStorage';
import { generateApproperiateURL } from '@/helpers/utils/urlHelpers';
import { logoutZendeskWidget } from '@/hooks/useZendeskAuth';
import { useAppDispatch } from '@/store';
import { useLogoutMutation } from '@/store/api/authentication';
import { customerApi } from '@/store/api/customer';
import { useGetShoppingCartQuery, useRemoveListFromCartMutation } from '@/store/api/shoppingCart';
import { setAuthenticationLoadingStatus, setAuthenticationStatus } from '@/store/slices/authentication';

import { removeSessionCookieWithDomainExclusion } from '../utils/idleTrackingUtils';

export const useLogout = (isBackoffice: boolean = false, webalias: string = '') => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const [logoutMutation] = useLogoutMutation();
  const { data: cartData } = useGetShoppingCartQuery(undefined);
  const cart = cartData?.data?.cart;
  const [removeListFromCart] = useRemoveListFromCartMutation();

  const logout = useCallback(async () => {
    console.log('useLogout: Starting logout process...', { isBackoffice, webalias });
    // Set loading state to true and keep it on (don't turn it off)
    dispatch(setAuthenticationLoadingStatus(true));

    try {
      // Remove enrollment kits (Basic Start / BYP) from cart before logout
      // so they don't persist and appear in simple checkout on next login
      try {
        if (cart && cart.length > 0) {
          const enrollmentPackItems = findEnrollmentPackItemsInCart(cart);
          if (enrollmentPackItems.length > 0) {
            const itemIds = enrollmentPackItems.map(item => item.itemId);
            await removeListFromCart({ ids: itemIds, removeForcefully: true }).unwrap();
          }
        }
        clearEnrollmentTracking();
      } catch (e) {
        console.warn('useLogout: Failed to remove enrollment pack items from cart:', e);
      }

      // Clear all localStorage items first
      try {
        // Clear 2FA temporary data
        localStorage.removeItem('__pending_2fa_customer_id');
        localStorage.removeItem('__pending_2fa_remember_me');

        // Clear all other stored data
        localStorage.clear();
      } catch (e) {
        console.error('Failed to clear localStorage:', e);
      }

      // Call the logout API
      console.log('useLogout: Calling logout API...');
      const response = await logoutMutation().unwrap();
      console.log('useLogout: Logout API response:', response);

      // For manual logout, use domain exclusion (not complete removal)
      removeSessionCookieWithDomainExclusion();
      // Clear authentication state
      dispatch(setAuthenticationStatus(false));

      // Invalidate customer data cache to fix user switching issue
      dispatch(customerApi.util.invalidateTags(['Customers']));

      // Logout Zendesk widget before redirect to ensure user context is cleared
      logoutZendeskWidget();

      // Redirect to appropriate login page (loading stays on)
      const loginUrl = isBackoffice ? '/login' : generateApproperiateURL('/account/login', webalias);
      // window.location.href = loginUrl;
      clearAllLocalStorage();
      router.push(loginUrl);
    } catch (error) {
      console.error('Logout failed:', error);
      // Even if API fails, clear local state and redirect
      dispatch(setAuthenticationStatus(false));

      try {
        clearAllLocalStorage();
      } catch (e) {
        console.error('Failed to clear localStorage:', e);
      }

      // Logout Zendesk widget before redirect to ensure user context is cleared
      logoutZendeskWidget();

      // Redirect to appropriate login page (loading stays on)
      const loginUrl = isBackoffice ? '/login' : generateApproperiateURL('/account/login', webalias);
      window.location.href = loginUrl;
    }
  }, [dispatch, isBackoffice, logoutMutation, webalias, router, cart, removeListFromCart]);

  return { logout };
};
